import { Grid2 } from "@mui/material";
import { useEffect, useMemo } from "react";
import useTrees from "../../../Hooks/useTrees";
import useUIBp from "../../../Hooks/useUIBoilerplate";
import useURLQuery from "../../../Hooks/useURLQuery";
import { useWorker } from "../../../Hooks/useWorker";
import { UIactions } from "../../../store/slices/UI";
import { treesActions } from "../../../store/slices/trees";
import { warehouseActions } from "../../../store/slices/warehouse";
import { workerActions } from "../../../store/slices/workers";
import { sortObjByKey } from "../../../utility/misc/sortObjByKey";
import { defaultPagesCount } from "../../UI/GeneralPurpose/Pagination";
import SlimAlert from "../../UI/GeneralPurpose/SlimAlert";
import {
  defaultFilter,
  defaultPagination,
} from "../../UI/GeneralPurpose/genericIDs";
import getChannelMessages, {
  channelMessagesDataId,
  channelMessagesTotalId,
  getChannelMessagesId,
} from "../Actions/getChannelMessages";
import { messageBodiesDataId } from "../Actions/getMessageBody";
import getTeams, { getTeamsId, teamsDataId } from "../Actions/getTeams";
import Core from "../Components/Core";

const UIText = {
  "es-CL":
    "La organización no tiene equipos de Microsoft Teams respaldados todavía",
  "en-US":
    "The organization does not have any Microsoft Teams teams backed up yet",
};

const Teams = () => {
  const { auth, dispatch, navigate, warehouse, UI, settings } = useUIBp();
  const trees = useTrees();
  const { teamId, channelId } = useURLQuery();
  const total = UI[channelMessagesTotalId] ?? 0;

  const { done: dT, working: wT, error: eT } = useWorker(getTeamsId);
  const { done: dM, working: wM, error: eM } = useWorker(getChannelMessagesId);

  const working = wT || wM;
  const done = dT && (channelId ? dM : true);
  const error = eT || eM;
  const workerId = !dT || eT ? getTeamsId : getChannelMessagesId;

  const info = UIText[settings.locale];

  const filter = UI.filter ?? "";
  const pagination = UI.pagination ?? { page: 0, count: 0, rowsPerPage: 0 };

  const query = useMemo(() => {
    const offset = defaultPagesCount * (pagination.page ?? 0);
    let baseQuery = "?";
    if (filter) {
      baseQuery += "searchInput=";

      const fromMatch = filter.match(/from:\(([^)]+)\)/);
      const fromValue = fromMatch ? fromMatch[1] : "";
      if (fromValue) {
        baseQuery += `from:{${fromValue}}`;
      }

      const subjectMatch = filter.match(/subject:\(([^)]+)\)/);
      const subjectValue = subjectMatch ? subjectMatch[1] : "";
      if (subjectValue) {
        baseQuery += `subject:{${subjectValue}}`;
      }

      const startDateMatch = filter.match(/\bstartDate:(\d+-\d+-\d+)\b/);
      const startDateValue = startDateMatch ? startDateMatch[1] : "";
      if (startDateValue) {
        let sDate = startDateValue.split("-");
        sDate = `${sDate[2]}-${sDate[1]}-${sDate[0]}`;
        baseQuery += `startDate:{${sDate}}`;
      }

      const endDateMatch = filter.match(/\bendDate:(\d+-\d+-\d+)\b/);
      const endDateValue = endDateMatch ? endDateMatch[1] : "";
      if (endDateValue) {
        let eDate = endDateValue.split("-");
        eDate = `${eDate[2]}-${eDate[1]}-${eDate[0]}`;
        baseQuery += `endDate:{${eDate}}`;
      }

      // Anything not captured by a known keyword (e.g. free text typed in the
      // general filter bar) is sent as in, the same way Mails does.
      const inValue = filter
        .replace(/from:\([^)]+\)/g, "") // Remove from:(...)
        .replace(/subject:\([^)]+\)/g, "") // Remove subject:(...)
        .replace(/\bstartDate:\d+-\d+-\d+\b/g, "") // Remove startDate:...
        .replace(/\bendDate:\d+-\d+-\d+\b/g, "") // Remove endDate:...
        .trim(); // Trim any extra spaces
      if (inValue) {
        baseQuery += `in:{${inValue}}`;
      }

      baseQuery += `&limit=${defaultPagesCount}&offset=${offset}`;
    } else {
      baseQuery += `limit=${defaultPagesCount}&offset=${offset}`;
    }
    return baseQuery;
  }, [filter, pagination.page]);

  // Bootstrap teams (and their channels) once.
  useEffect(() => {
    if (!dT) {
      const controller = new AbortController();
      dispatch(getTeams({ ...auth, controller }));
      return () => controller.abort();
    }
  }, [dispatch, auth, dT]);

  // After teams load, redirect to the first team/channel if URL is empty
  // or stale.
  useEffect(() => {
    if (!dT || eT) return;

    const teamsArr = warehouse[teamsDataId] ?? [];
    if (!teamsArr.length) return;

    const targetTeam = teamsArr.find((t) => t.id === teamId) ?? teamsArr[0];
    const teamTree = trees[`teams-${targetTeam.id}`] ?? [];
    const channels = teamTree.slice(1);

    const targetChannel =
      channels.find((c) => c.id === channelId) ?? channels[0];

    if (!targetChannel) {
      // Channels still loading for this team — let the next render handle it.
      if (teamId !== targetTeam.id) {
        navigate(`/dashboard/teams?teamId=${targetTeam.id}`);
      }
      return;
    }

    if (targetTeam.id !== teamId || targetChannel.id !== channelId) {
      navigate(
        `/dashboard/teams?teamId=${targetTeam.id}&channelId=${targetChannel.id}`
      );
    }
  }, [dT, eT, warehouse, trees, teamId, channelId, navigate]);

  // Fetch messages whenever the active channel or query changes.
  useEffect(() => {
    if (channelId) {
      const controller = new AbortController();
      dispatch(
        getChannelMessages({ ...auth, channelId, query, controller })
      );
      return () => controller.abort();
    }
  }, [dispatch, auth, channelId, query]);

  // Reset paging/filter when switching channels.
  useEffect(() => {
    dispatch(UIactions.clear(defaultPagination));
    dispatch(UIactions.clear(defaultFilter));
    dispatch(workerActions.clear(getChannelMessagesId));
    dispatch(warehouseActions.unload(channelMessagesDataId));
    dispatch(warehouseActions.unload(messageBodiesDataId));
    dispatch(workerActions.clearGroup("getMessageBody-"));
    dispatch(UIactions.clear(channelMessagesTotalId));
  }, [dispatch, channelId]);

  const messages = useMemo(() => {
    const msgs = warehouse[channelMessagesDataId] ?? [];
    // Conversation order: oldest first, like the Teams UI.
    return [...msgs].sort((a, b) =>
      sortObjByKey(a, b, "created_date_time", false)
    );
  }, [warehouse]);

  useEffect(() => {
    dispatch(UIactions.setPagination({ count: total }));
  }, [dispatch, total]);

  useEffect(() => {
    return () => {
      dispatch(UIactions.clear(defaultPagination));
      dispatch(UIactions.clear(defaultFilter));
      dispatch(UIactions.clear(channelMessagesTotalId));
      dispatch(treesActions.removeTreeGroup("teams-"));
      dispatch(warehouseActions.unload(channelMessagesDataId));
      dispatch(warehouseActions.unload(messageBodiesDataId));
      dispatch(warehouseActions.unload(teamsDataId));
      dispatch(workerActions.clear(getTeamsId));
      dispatch(workerActions.clear(getChannelMessagesId));
      dispatch(workerActions.clearGroup("getChannels-"));
      dispatch(workerActions.clearGroup("getMessageBody-"));
    };
  }, [dispatch]);

  const teamsArr = warehouse[teamsDataId] ?? [];
  if (dT && !eT && !teamsArr.length) {
    return (
      <Grid2 container {...{ size: 12, justifyContent: "center", padding: 10 }}>
        <SlimAlert notCollapse {...{ severity: "info", errors: [info] }} />
      </Grid2>
    );
  }

  return (
    <Core
      {...{
        messages,
        done,
        working,
        error,
        workerId,
      }}
    />
  );
};

export default Teams;
